import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Fade } from '@/components/ui/fade';
import { EmptyState } from '@/components/ui/empty-state';
import { useMinLoading } from '@/hooks/use-min-loading';
import { useComunicadosList } from '../hooks/use-comunicados-list';
import type { ComunicadoListItem } from '../types-comunicados';
import { ComunicadosListRow } from './comunicados-list-row';
import { ComunicadosListSkeleton } from './comunicados-list-skeleton';

const HEAD = 'flex items-center justify-between';
const TITLE = 'font-display text-lg font-bold text-text-primary';
const NEW_BTN =
  'inline-flex items-center gap-xs rounded-md bg-brand-red px-md py-sm text-sm font-medium text-white transition-colors hover:bg-brand-coral';
const LIST = 'rounded-lg border border-white/[0.06] bg-surface/40 px-md py-sm';

type ListProps = {
  showSkeleton: boolean;
  items: ComunicadoListItem[] | undefined;
};

function ListContent({ showSkeleton, items }: ListProps): ReactNode {
  if (showSkeleton) return <ComunicadosListSkeleton />;
  if (!items || items.length === 0) {
    return <EmptyState title="No hay comunicados todavía. Crea el primero." />;
  }
  return (
    <ul className={LIST}>
      {items.map((item) => (
        <ComunicadosListRow key={item.id} item={item} />
      ))}
    </ul>
  );
}

export function ComunicadosListPage(): ReactNode {
  const nav = useNavigate();
  const { data, isLoading } = useComunicadosList();
  const minLoading = useMinLoading(isLoading);
  const showSkeleton = isLoading || minLoading;
  return (
    <div className="mx-auto max-w-[720px] space-y-lg">
      <div className={HEAD}>
        <h1 className={TITLE}>Comunicados</h1>
        <button
          onClick={() => nav('/admin/communications/new')}
          className={NEW_BTN}
        >
          <Plus size={14} /> Nuevo
        </button>
      </div>
      <Fade activeKey={showSkeleton ? 'skeleton' : 'data'}>
        <ListContent showSkeleton={showSkeleton} items={data} />
      </Fade>
    </div>
  );
}
